import React, { useEffect } from "react";
import DropDown, {
  DropDownItem,
  DropDownSubMenu,
} from "components/shared/DropDown";
import { faLanguage } from "@fortawesome/pro-light-svg-icons";
import { useTranslationLanguages } from "hooks/useTranslationLanguages";
import { useTranslationConfig } from "hooks/useTranslationConfig";
import { QueryClientProvider } from "react-query";
import queryClient from "helpers/queryClient";
import LanguageListItem from "components/shared/LanguageListItem";

interface TranslationDropDownProps {
  handleLanguageSelect: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    language: string,
  ) => void;
  object_language?: string;
}

export const ORIGINAL_LANGUAGE_SIGNIFIER = "ORIGINAL";

export default function TranslationDropDown({
  handleLanguageSelect,
  object_language,
}: TranslationDropDownProps) {
  const {
    primaryLanguages,
    secondaryLanguages,
    selectedLanguage,
    setSelectedLanguage,
  } = useTranslationLanguages();
  const { isTranslationEnabled } = useTranslationConfig();

  useEffect(() => {
    setSelectedLanguage(object_language || ORIGINAL_LANGUAGE_SIGNIFIER);
  }, [object_language]);

  if (!isTranslationEnabled) return;

  return (
    <div className="translation-controls">
      <DropDown.Toggle
        className="translate-button text-muted -m-2"
        icon={faLanguage}
        buttonType="normal"
        small
      />
      <DropDown>
        {selectedLanguage !== ORIGINAL_LANGUAGE_SIGNIFIER && (
          <DropDownItem>
            <a
              className="dropdown-item translate-menu-item"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                handleLanguageSelect(event, ORIGINAL_LANGUAGE_SIGNIFIER);
                setSelectedLanguage(ORIGINAL_LANGUAGE_SIGNIFIER);
              }}
            >
              {I18n.t(
                "js.activitystreams.activity.translation_controls.show_original",
              )}
            </a>
          </DropDownItem>
        )}
        {primaryLanguages.map((lang) => (
          <DropDownItem key={lang.language}>
            <LanguageListItem
              lang={lang}
              selectedLanguage={selectedLanguage}
              handleClick={handleLanguageSelect}
              setSelectedLanguage={setSelectedLanguage}
            />
          </DropDownItem>
        ))}
        {secondaryLanguages.length > 0 && (
          <DropDownSubMenu
            text={I18n.t(
              "js.activitystreams.activity.translation_controls.more",
            )}
            side={window.isApp ? "left" : "right"}
          >
            {secondaryLanguages.map((lang) => (
              <DropDownItem key={lang.language}>
                <LanguageListItem
                  lang={lang}
                  selectedLanguage={selectedLanguage}
                  handleClick={handleLanguageSelect}
                  setSelectedLanguage={setSelectedLanguage}
                />
              </DropDownItem>
            ))}
          </DropDownSubMenu>
        )}
      </DropDown>
    </div>
  );
}

export function StandaloneTranslationDropDown({
  handleLanguageSelect,
  object_language,
}: TranslationDropDownProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationDropDown
        handleLanguageSelect={handleLanguageSelect}
        object_language={object_language}
      />
    </QueryClientProvider>
  );
}
